import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  faCheck,
  faChevronDown,
  faGripVertical,
  faLink,
  faLinkSlash,
  faPlus,
  faStar,
  faTrashCan
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import { Button, Checkbox, Input } from '@/ui';

import type { FieldDef } from '../types';

import canvasLayout from './canvasLayout';
import { badgeClasses, fieldTypeMeta, previewText } from './fieldTypes';
import type { BuilderState } from './useBuilderState';

interface BuilderCanvasProps {
  state: BuilderState;
  onGroup: () => void;
  onSaveSection: (sectionKey: string) => void;
  onToggleReusable: (field: FieldDef) => void;
}

const FieldRow = ({
  field,
  state,
  onToggleReusable
}: {
  field: FieldDef;
  state: BuilderState;
  onToggleReusable: (field: FieldDef) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.key
  });
  const active = state.selectedFieldKey === field.key;
  const checked = state.checkedKeys.has(field.key);
  // A saved field always carries a questionId, private fork included, so the
  // library badge keys off inLibrary.
  const reusable = !!field.inLibrary;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      onClick={() => state.select(field.key)}
      className={`relative cursor-pointer rounded-xl bg-white p-3.5 transition dark:bg-uniform-gray-900 ${
        active
          ? 'border border-blue-500 ring-2 ring-blue-200 dark:border-blue-400 dark:ring-blue-900'
          : 'border border-uniform-gray-200 dark:border-uniform-gray-700'
      } ${isDragging ? 'opacity-60' : ''}`}>
      <div className="mb-2 flex items-center gap-2.5">
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            id={`select-${field.key}`}
            label={`Select ${field.label || field.key}`}
            hideLabel
            className="flex-none"
            checked={checked}
            onChange={() => state.toggleChecked(field.key)}
          />
        </span>
        <Button
          size="icon-xs"
          variant="quiet"
          color="neutral"
          aria-label={`Reorder ${field.label || field.key}`}
          className="flex-none cursor-grab"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}>
          <FontAwesomeIcon icon={faGripVertical} className="text-sm" />
        </Button>
        <span
          className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide ${badgeClasses(field.widget)}`}>
          {fieldTypeMeta(field.widget).badge}
        </span>
        {reusable && (
          <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-blue-600 dark:bg-blue-950/30 dark:text-blue-300">
            <FontAwesomeIcon icon={faStar} className="text-[8px]" />
            IN LIBRARY
          </span>
        )}
        <div className="flex-1" />
        <Button
          size="icon-sm"
          variant={reusable ? 'ghost' : 'quiet'}
          color="secondary"
          title="Mark reusable"
          aria-label={reusable ? 'Remove from question library' : 'Save to question library'}
          aria-pressed={reusable}
          onClick={(e) => {
            e.stopPropagation();
            onToggleReusable(field);
          }}>
          <FontAwesomeIcon icon={faStar} className="text-sm" />
        </Button>
        <Button
          size="icon-sm"
          variant="quiet"
          color="destructive"
          aria-label={`Remove ${field.label || field.key}`}
          onClick={(e) => {
            e.stopPropagation();
            state.removeField(field.key);
          }}>
          <FontAwesomeIcon icon={faTrashCan} className="text-sm" />
        </Button>
      </div>
      <div className="mb-1.5 flex items-center gap-1">
        <span className="text-[13.5px] font-semibold text-uniform-gray-800 dark:text-uniform-gray-100">
          {field.label || field.key}
        </span>
        {field.required && (
          <span className="text-[13px] font-bold text-red-500 dark:text-red-400">*</span>
        )}
      </div>
      <div className="pointer-events-none rounded-lg border border-uniform-gray-200 px-3 py-2 text-[13.5px] text-uniform-gray-400 dark:border-uniform-gray-700 dark:text-uniform-gray-500">
        {previewText(field)}
      </div>
    </div>
  );
};

const BuilderCanvas = ({ state, onGroup, onSaveSection, onToggleReusable }: BuilderCanvasProps) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const blocks = canvasLayout(state.fields, state.sections);
  const selectionCount = state.checkedKeys.size;

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      state.reorderFields(String(active.id), String(over.id));
    }
  };

  return (
    <div className="flex min-h-0 flex-col bg-uniform-gray-50 dark:bg-uniform-gray-900">
      {selectionCount > 0 && (
        <div className="flex items-center justify-between border-b border-blue-100 bg-blue-50 px-[22px] py-2.5 dark:border-blue-900 dark:bg-blue-950/30">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-blue-700 dark:text-blue-300">
            <FontAwesomeIcon icon={faCheck} />
            {selectionCount} field{selectionCount === 1 ? '' : 's'} selected
          </div>
          <div className="flex items-center gap-2">
            <Button size="xs" color="primary" onClick={onGroup}>
              Group into section
            </Button>
            <Button size="xs" variant="ghost" color="neutral" onClick={state.clearChecked}>
              Clear
            </Button>
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-[22px] pb-10 pt-[22px]">
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <SortableContext
            items={state.fields.map((f) => f.key)}
            strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-[18px]">
              {blocks.length === 0 && (
                <p className="rounded-xl border border-dashed border-uniform-gray-200 py-10 text-center text-[13px] text-uniform-gray-400 dark:border-uniform-gray-700 dark:text-uniform-gray-500">
                  Add a field from the library to get started.
                </p>
              )}
              {blocks.map((block) => {
                if (block.kind === 'field') {
                  return (
                    <FieldRow
                      key={block.field.key}
                      field={block.field}
                      state={state}
                      onToggleReusable={onToggleReusable}
                    />
                  );
                }
                const { section, fields } = block;
                const open = !collapsed[section.key];
                const name = section.title || section.key;
                const linked = !!section.sectionId;
                const targeted = state.activeSectionKey === section.key;
                return (
                  <div
                    key={section.key}
                    className={`rounded-xl bg-white dark:bg-uniform-gray-900 ${
                      targeted
                        ? 'border border-blue-400 ring-2 ring-blue-100 dark:border-blue-500 dark:ring-blue-900'
                        : 'border border-uniform-gray-200 dark:border-uniform-gray-700'
                    }`}>
                    <div className="flex items-center gap-2 border-b border-uniform-gray-100 px-3.5 py-3 dark:border-uniform-gray-700">
                      <Button
                        size="icon-xs"
                        variant="quiet"
                        color="neutral"
                        aria-expanded={open}
                        aria-label={`${open ? 'Collapse' : 'Expand'} ${name}`}
                        className="flex-none"
                        onClick={() =>
                          setCollapsed((c) => ({ ...c, [section.key]: !c[section.key] }))
                        }>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={`text-xs transition ${open ? '' : '-rotate-90'}`}
                        />
                      </Button>
                      {/* A linked section's title belongs to its master, so it
                          only reads back here; an ad-hoc one is renamed in place. */}
                      {linked ? (
                        <span className="text-sm font-bold text-uniform-gray-900 dark:text-uniform-gray-100">
                          {name}
                        </span>
                      ) : (
                        <Input
                          id={`section-title-${section.key}`}
                          label={`Section title for ${name}`}
                          hideLabel
                          className="max-w-[220px]"
                          value={section.title}
                          placeholder={section.key}
                          onChange={(e) => state.renameSection(section.key, e.target.value)}
                        />
                      )}
                      {linked && (
                        <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-blue-600 dark:bg-blue-950/30 dark:text-blue-300">
                          <FontAwesomeIcon icon={faLink} className="text-[9px]" />
                          Linked to library
                        </span>
                      )}
                      <span className="ml-auto text-[11.5px] text-uniform-gray-400 dark:text-uniform-gray-500">
                        {fields.length} fields
                      </span>
                      {/* A linked section's members come from its master, so
                          there is nothing to add into here — unlink first. */}
                      {!linked && (
                        <Button
                          size="xs"
                          variant={targeted ? 'subtle' : 'ghost'}
                          color={targeted ? 'primary' : 'neutral'}
                          aria-pressed={targeted}
                          aria-label={
                            targeted ? `Stop adding fields to ${name}` : `Add fields to ${name}`
                          }
                          onClick={() => state.targetSection(targeted ? null : section.key)}>
                          <FontAwesomeIcon icon={faPlus} className="text-[11px]" />
                          {targeted ? 'Adding here' : 'Add fields'}
                        </Button>
                      )}
                      {linked ? (
                        <Button
                          size="xs"
                          variant="ghost"
                          color="neutral"
                          aria-label={`Unlink section ${name} from the library`}
                          onClick={() => state.unlinkSection(section.key)}>
                          <FontAwesomeIcon icon={faLinkSlash} className="text-[11px]" />
                          Unlink
                        </Button>
                      ) : (
                        <Button
                          size="xs"
                          variant="ghost"
                          color="neutral"
                          onClick={() => onSaveSection(section.key)}>
                          Save to library
                        </Button>
                      )}
                      <Button
                        size="icon-sm"
                        variant="quiet"
                        color="destructive"
                        aria-label={`Remove section ${name}`}
                        onClick={() => state.removeSection(section.key)}>
                        <FontAwesomeIcon icon={faTrashCan} className="text-sm" />
                      </Button>
                    </div>
                    {open && (
                      <div className="flex flex-col gap-2.5 p-3.5">
                        {fields.map((field) => (
                          <FieldRow
                            key={field.key}
                            field={field}
                            state={state}
                            onToggleReusable={onToggleReusable}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default BuilderCanvas;
